'use client';

import { FC } from 'react';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import '@splidejs/react-splide/css';
import '@/styles/splide-slider.scss';
import { useDeviceDetect } from '@/utils/useDeviceDetect';
import { useStore } from 'zustand';
import { useMapStore } from '@/stores/map/mapStore';
import WhereToBuyBtn from '@/components/button/where-to-buy-btn';
import { FaSnowflake, FaSun, FaBolt, FaCamera } from 'react-icons/fa';
import Button from '../common/Button';

type Product = {
  id: number;
  name: string;
  coolingCapacity: string;
  heatingCapacity: string;
  powerSupply: string;
  price: number;
  rebateAmount: number;
  buttonType: 'where-to-buy' | 'notify-me';
  image: {
    data: Array<{
      attributes: {
        url: string;
        alternativeText: string | null;
      };
    }>;
  };
};

interface PurchaseSectionDynamicProps {
  content: {
    text: string;
    products: Product[];
  };
}

const PurchaseSectionDynamic: FC<PurchaseSectionDynamicProps> = ({
  content,
}) => {
  const isMobile = useDeviceDetect();
  const { setModalMapOpen } = useStore(useMapStore, (state) => ({
    setModalMapOpen: state.setModalMapOpen,
  }));

  return (
    <div className='mx-10 my-20'>
      <div className='purchase-section__title mx-auto max-w-[440px] text-center text-4xl font-bold text-black'>
        {content.text}
      </div>
      <div className='purchase-section relative mt-[35px] w-full rounded-3xl bg-gray-100 pt-10'>
        <div className='purchase-section__container container mx-auto px-4'>
          <div className='purchase-section__slider-container flex w-full justify-center pb-8'>
            <div className='purchase-section__slider-wrapper w-full max-w-[1200px] px-4 sm:px-0'>
              <Splide
                options={{
                  type: 'loop',
                  perPage: 2,
                  gap: '1em',
                  pagination: true,
                  arrows: true,
                  drag: 'free',
                  padding: '20%',
                  breakpoints: {
                    1024: {
                      perPage: 1,
                      pagination: true,
                      padding: '16%',
                    },
                    768: {
                      perPage: 1,
                      pagination: true,
                      padding: '16%',
                    },
                    640: {
                      perPage: 1,
                      pagination: true,
                      padding: '2%',
                    },
                  },
                }}
                aria-label='Product Selection'
              >
                {content.products.map((product) => (
                  <SplideSlide key={product.id}>
                    <div className='purchase-section__product-card mx-auto w-full max-w-[376px] rounded-[20px] border border-gray-200 bg-white p-9 shadow-sm'>
                      <div className='purchase-section__product-image aspect-square w-full overflow-hidden'>
                        <img
                          src={product.image.data[0].attributes.url}
                          alt={
                            product.image.data[0].attributes.alternativeText ||
                            product.name
                          }
                          className='h-full w-full object-contain'
                        />
                      </div>

                      <div className='purchase-section__product-details mt-6 flex w-full flex-col items-stretch justify-between gap-4'>
                        <h3 className='purchase-section__product-name text-center text-xl font-bold text-black'>
                          {product.name}
                        </h3>

                        <div className='purchase-section__specifications flex flex-col gap-3'>
                          <div className='purchase-section__spec-item flex items-center gap-2 text-gray-600'>
                            <FaSnowflake className='text-primary' />
                            <span>Cooling capacity</span>
                          </div>
                          <p className='pl-6 text-left'>
                            {product.coolingCapacity}
                          </p>

                          <div className='purchase-section__spec-item flex items-center gap-2 text-gray-600'>
                            <FaSun className='text-orange-500' />
                            <span>Heating capacity</span>
                          </div>
                          <p className='pl-6 text-left'>
                            {product.heatingCapacity}
                          </p>

                          <div className='purchase-section__spec-item flex items-center gap-2 text-gray-600'>
                            <FaBolt className='text-yellow-500' />
                            <span>Power supply</span>
                          </div>
                          <p className='pl-6 text-left'>
                            {product.powerSupply}
                          </p>
                        </div>

                        {product.price > 0 ? (
                          <div className='purchase-section__price mt-4'>
                            <p className='text-gray-600'>As low as</p>
                            <p className='text-2xl font-bold text-black'>
                              ${product.price.toLocaleString()}
                            </p>
                          </div>
                        ) : (
                          <div className='mt-4'></div>
                        )}

                        {product.rebateAmount > 0 && (
                          <Button className='purchase-section__rebate-btn mt-4 flex items-center justify-between bg-white px-4 py-3 text-black hover:bg-gray-200'>
                            <div className='flex items-center gap-2'>
                              <FaCamera className='text-primary' />
                              <span>Rebates</span>
                            </div>
                            <span className='text-primary'>
                              Up to ${product.rebateAmount}
                            </span>
                          </Button>
                        )}

                        <div className='purchase-section__where-to-buy w-full'>
                          {product.buttonType === 'where-to-buy' ? (
                            <WhereToBuyBtn
                              isMobile={isMobile}
                              setMapOpen={setModalMapOpen}
                              title='Where to Buy'
                              color='bg-primary text-white hover:bg-primary-dark'
                              className='pill-button-full'
                            />
                          ) : (
                            <Button className='w-full rounded-full border border-black bg-white px-6 py-2 text-black hover:bg-gray-100'>
                              Notify Me
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </SplideSlide>
                ))}
              </Splide>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseSectionDynamic;
